import React from "react";
import PropTypes from "prop-types";
import NeoCard from "../../../components/Common/NeoCard";

/**
 * PlatformStatCard — Card reutilizável para KPIs com design outonal premium.
 * Integrado ao Neo Design System utilizando o NeoCard.
 *
 * @param {string} icon - Classe do ícone Phosphor/MDI
 * @param {string} label - Texto descritivo
 * @param {string|number} value - Valor do KPI
 * @param {string} color - Variante de cor: primary, success, warning, danger, info, purple
 * @param {function} onClick - Opcional; quando informado o card vira um atalho clicável
 * @param {boolean} active - Opcional; destaca o card com borda quando um filtro vinculado está ativo
 */
const PlatformStatCard = ({ icon, label, value, color = "primary", onClick, active = false }) => {
    // Mapeamento de cores de texto e borda dos ícones
    const themeColors = {
        primary: {
            text: "var(--color-brand-primary)", // Chocolate
            bg: "rgba(72, 39, 18, 0.08)",
            border: "rgba(72, 39, 18, 0.20)"
        },
        success: {
            text: "#10b981", // Verde
            bg: "rgba(16, 185, 129, 0.08)",
            border: "rgba(16, 185, 129, 0.20)"
        },
        warning: {
            text: "#d97706", // Laranja queimado / Bronze
            bg: "rgba(217, 119, 6, 0.08)",
            border: "rgba(217, 119, 6, 0.20)"
        },
        danger: {
            text: "#ef4444", // Vermelho
            bg: "rgba(239, 68, 68, 0.08)",
            border: "rgba(239, 68, 68, 0.20)"
        },
        info: {
            text: "var(--color-brand-accent)", // Laranja destaque
            bg: "rgba(226, 102, 28, 0.08)",
            border: "rgba(226, 102, 28, 0.20)"
        },
        purple: {
            text: "var(--color-brand-secondary)", // Bronze escuro
            bg: "rgba(120, 75, 34, 0.08)",
            border: "rgba(120, 75, 34, 0.20)"
        }
    };

    const colorSet = themeColors[color] || themeColors.primary;

    return (
        <NeoCard
            thinBorder={true}
            bodyClassName="p-3 d-flex align-items-center gap-3"
            style={{
                minHeight: "95px",
                cursor: onClick ? "pointer" : "default",
                ...(active ? { border: `2px solid ${colorSet.text}`, boxShadow: `0 0 0 3px ${colorSet.bg}` } : {}),
            }}
            className="animate__animated animate__fadeIn animate__faster"
            onClick={onClick}
            title={onClick ? "Ver detalhes" : undefined}
        >
            {/* Ícone outonal circular/quadrado suavizado */}
            <div 
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "10px",
                    backgroundColor: colorSet.bg,
                    border: `1.5px solid ${colorSet.border}`,
                    color: colorSet.text
                }}
            >
                <i className={`${icon} font-size-22`} style={{ color: colorSet.text }}></i>
            </div>
            
            {/* Informações de Texto do KPI */}
            <div className="flex-grow-1 min-width-0">
                <small 
                    className="text-muted font-size-10 fw-bold text-uppercase d-block mb-0.5 text-truncate" 
                    style={{ letterSpacing: "0.5px" }}
                    title={label}
                >
                    {label}
                </small>
                <h4 className="fw-bold mb-0 text-dark text-truncate" style={{ fontFamily: "Outfit, sans-serif" }} title={value}>
                    {value}
                </h4>
            </div>
        </NeoCard>
    );
};

PlatformStatCard.propTypes = {
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    color: PropTypes.oneOf(["primary", "success", "warning", "danger", "info", "purple"]),
    onClick: PropTypes.func,
    active: PropTypes.bool
};

export default PlatformStatCard;
